import { motion } from 'framer-motion';
import { AlertCircle, Clock, Cloud, Moon, Sun } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next'; // ADD THIS IMPORT
import LoadingComponent from '../../../shared/components/LoadingComponent';

interface TimeGroups {
  morning: string[];
  afternoon: string[];
  evening: string[];
}

interface TimeSlotsComponentProps {
  fetching: boolean;
  error: any;
  timeGroups: TimeGroups | null;
  hasAvailableSlots: boolean;
  selectedTime: string | null;
  setSelectedTime: (time: string | null) => void;
  colors?: {
    timeGroupText?: string;
    timeslotBackground?: string;
    timeslotBorder?: string;
    timeslotText?: string;
    selectedTimeslotBackground?: string;
    selectedTimeslotText?: string;
    [key: string]: string | undefined;
  };
}

const TimeSlotsComponent: React.FC<TimeSlotsComponentProps> = ({
  fetching,
  error,
  timeGroups,
  hasAvailableSlots,
  selectedTime,
  setSelectedTime,
  colors = {}
}) => {
  const { t } = useTranslation(); // ADD THIS LINE

  const getHoverShadowStyle = () => {
       return `0 0 20px ${colors.selectedTimeslotShadow || 'rgba(34,197,94,0.3)'}`;
    };

  const renderTimeSlots = (slots: string[], title: string, icon: React.ReactNode) => (
    <div className="lp-mb-4">
      <h4
        className="lp-text-lg lp-font-medium lp-mb-2 lp-flex lp-items-center"
        style={{ color: colors.timeGroupText || '#DDB889' }}
      >
        {icon}
        <span className="lp-ml-2">{title}</span>
      </h4>
      <div className="lp-grid lp-grid-cols-2 sm:lp-grid-cols-3 lp-gap-2">
        {slots.map((slot: string) => (
          <motion.button
            key={slot}
            onClick={() => setSelectedTime(slot)}
            whileHover={{
              boxShadow: getHoverShadowStyle()
            }}
            whileTap={{ scale: 0.98 }}
            className="group lp-relative lp-p-2 lp-rounded-xl lp-flex lp-items-center lp-justify-center lp-transition-all lp-duration-200 lp-backdrop-blur-sm lp-overflow-hidden"
            style={{
  backgroundColor: selectedTime === slot
    ? (colors.selectedTimeslotBackground || 'rgba(0,0,0,0.95)')  // Same background
    : (colors.timeslotBackground || 'rgba(0,0,0,0.95)'),
  border: '1px solid',
  borderColor: selectedTime === slot
    ? (colors.selectedTimeslotBorder || '#22c55e')
    : (colors.timeslotBorder || 'rgba(221,184,137,0.1)'),
  color: selectedTime === slot
    ? (colors.selectedTimeslotText || '#22c55e')  // ✨ Green text!
    : (colors.timeslotText || '#DDB889'),
  boxShadow: selectedTime === slot
    ? `0 0 15px ${colors.selectedTimeslotShadow || 'rgba(34,197,94,0.3)'}`
    : 'none'
}}
          >
            <span className="lp-relative lp-z-10 lp-flex lp-items-center">
              <Clock className="lp-mr-2" size={16} />
              {slot}
            </span>
            {selectedTime !== slot && (
              <div className="lp-absolute lp-inset-0 lp-z-0">
                <div
                  className="
                    lp-absolute lp-inset-0
                    lp-translate-x-[-100%]
                    lp-bg-gradient-to-r lp-from-transparent lp-via-[#DDB889]/10 lp-to-transparent
                    group-hover:lp-translate-x-[100%]
                    lp-transition-transform lp-duration-700 lp-ease-in-out
                  "
                />
              </div>
            )}
          </motion.button>
        ))}
      </div>
    </div>
  );

  if (fetching) {
    return (
      <div className="lp-flex lp-items-center lp-justify-center lp-h-[300px]">
        <LoadingComponent />
      </div>
    );
  }

  if (error) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="lp-flex lp-items-center lp-justify-center lp-h-[300px] lp-text-red-500"
      >
        <AlertCircle className="lp-mr-2" size={16} />
        {t('datetime.error')}: {error.message} {/* CHANGED: "Error:" */}
      </motion.div>
    );
  }

  if (!hasAvailableSlots) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="lp-text-center lp-p-6 lp-border lp-border-[#DDB889]/10 lp-rounded-2xl
          lp-bg-black/95 lp-backdrop-blur-sm"
      >
        <AlertCircle className="lp-mx-auto lp-mb-2 lp-text-[#DDB889]" size={24} />
        <p className="lp-text-lg lp-mb-2 lp-text-[#DDB889]">
          {t('datetime.noAvailableTimeSlots')} {/* CHANGED: "No available time slots for this date." */}
        </p>
        <p className="lp-text-sm lp-text-gray-400">
          {t('datetime.tryDifferentDate')} {/* CHANGED: "Please try selecting a different date." */}
        </p>
      </motion.div>
    );
  }

  // Make sure timeGroups exists before rendering
  if (!timeGroups) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      {timeGroups.morning && timeGroups.morning.length > 0 &&
        renderTimeSlots(timeGroups.morning, t('datetime.morning'), <Sun size={16} />)} {/* CHANGED: "Morning" */}
      {timeGroups.afternoon && timeGroups.afternoon.length > 0 &&
        renderTimeSlots(timeGroups.afternoon, t('datetime.afternoon'), <Cloud size={16} />)} {/* CHANGED: "Afternoon" */}
      {timeGroups.evening && timeGroups.evening.length > 0 &&
        renderTimeSlots(timeGroups.evening, t('datetime.evening'), <Moon size={16} />)} {/* CHANGED: "Evening" */}
    </motion.div>
  );
};

export default TimeSlotsComponent;
